import React, { useEffect, useMemo, useState } from 'react';
import { colors, radius, shadows } from '../../styles/theme';
import { logError } from '../../utils/logError';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const money = (value) => `${Number(value || 0).toLocaleString('tr-TR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})} ₺`;

const dateText = (value) => {
  if (!value) return '-';
  const s = String(value).slice(0, 10);
  const [y, m, d] = s.split('-');
  return y && m && d ? `${d}.${m}.${y}` : s;
};

const authHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const MusterilerTedarikcilerPage = () => {
  const [type, setType] = useState('tedarikci');
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [movements, setMovements] = useState([]);
  const [movementLoading, setMovementLoading] = useState(false);
  const [movementError, setMovementError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const headers = authHeaders();
        const [customerRes, supplierRes] = await Promise.all([
          fetch(`${API}/api/cari?tip=MUSTERI&with_usage=true`, { headers }),
          fetch(`${API}/api/tedarikciler`, { headers }),
        ]);

        const customerData = customerRes.ok ? await customerRes.json() : [];
        const supplierData = supplierRes.ok ? await supplierRes.json() : [];
        if (!cancelled) {
          setCustomers(Array.isArray(customerData) ? customerData.filter(x => x.aktif !== false) : []);
          setSuppliers(Array.isArray(supplierData) ? supplierData.filter(x => x.aktif !== false) : []);
        }
      } catch (err) {
        logError('Muhasebe cari listeleri yüklenemedi:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    setSelected(null);
    setMovements([]);
    setMovementError('');
  }, [type]);

  const list = type === 'musteri' ? customers : suppliers;
  const filtered = useMemo(() => {
    const q = query.trim().toLocaleLowerCase('tr-TR');
    if (!q) return list;
    return list.filter(item => [item.unvan, item.ad, item.kod, item.vergi_no, item.telefon]
      .filter(Boolean)
      .some(v => String(v).toLocaleLowerCase('tr-TR').includes(q)));
  }, [list, query]);

  const loadSupplierMovements = async (item) => {
    const res = await fetch(`${API}/api/finance/suppliers/${item.id}/ekstre`, { headers: authHeaders() });
    if (!res.ok) throw new Error(`Tedarikçi hareketleri alınamadı (${res.status})`);
    const data = await res.json();
    const rows = Array.isArray(data?.hareketler) ? data.hareketler : [];
    return rows.map(h => ({
      key: `s-${h.id}-${h.kaynak_id || ''}`,
      tarih: h.tarih,
      tip: h.tip || h.tur || h.kaynak_tipi || '-',
      belge: h.belge_no || h.fatura_no || h.referans_no || h.kaynak_no || '-',
      aciklama: h.aciklama || h.detay || h.notlar || '-',
      borc: Number(h.borc || 0),
      alacak: Number(h.alacak || 0),
      bakiye: h.bakiye_sonrasi,
    }));
  };

  const loadCustomerMovements = async (item) => {
    const headers = authHeaders();
    const [invoiceRes, receiptRes] = await Promise.all([
      fetch(`${API}/api/satis-faturalari?cari_id=${item.id}`, { headers }),
      fetch(`${API}/api/tahsilatlar?cari_id=${item.id}`, { headers }),
    ]);
    if (!invoiceRes.ok) throw new Error(`Satış faturaları alınamadı (${invoiceRes.status})`);
    if (!receiptRes.ok) throw new Error(`Tahsilatlar alınamadı (${receiptRes.status})`);

    const invoices = await invoiceRes.json();
    const receipts = await receiptRes.json();
    const rows = [
      ...(Array.isArray(invoices) ? invoices : []).map(f => ({
        key: `f-${f.id}`,
        tarih: f.tarih,
        tip: 'SATIŞ FATURASI',
        belge: f.fatura_no || '-',
        aciklama: f.notlar || 'Satış faturası',
        borc: Number(f.toplam_brut || 0),
        alacak: 0,
      })),
      ...(Array.isArray(receipts) ? receipts : []).map(t => ({
        key: `t-${t.id}`,
        tarih: t.tarih,
        tip: 'TAHSİLAT',
        belge: t.tahsilat_no || t.referans_no || '-',
        aciklama: t.notlar || t.yontem || 'Tahsilat',
        borc: 0,
        alacak: Number(t.tutar || 0),
      })),
    ].sort((a, b) => String(a.tarih || '').localeCompare(String(b.tarih || '')));

    let balance = 0;
    const withBalance = rows.map(row => {
      balance += row.borc - row.alacak;
      return { ...row, bakiye: balance };
    });
    return withBalance.reverse();
  };

  const selectItem = async (item) => {
    setSelected(item);
    setMovementLoading(true);
    setMovementError('');
    setMovements([]);
    try {
      const rows = type === 'tedarikci'
        ? await loadSupplierMovements(item)
        : await loadCustomerMovements(item);
      setMovements(rows);
    } catch (err) {
      logError('Cari hareketleri yüklenemedi:', err);
      setMovementError(err.message || 'Hareketler yüklenemedi');
    } finally {
      setMovementLoading(false);
    }
  };

  return (
    <div style={{ padding: '20px 28px 36px', background: colors.background, minHeight: 'calc(100vh - 120px)' }}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button
          onClick={() => setType('musteri')}
          style={switchStyle(type === 'musteri')}
        >
          👥 Müşteriler
        </button>
        <button
          onClick={() => setType('tedarikci')}
          style={switchStyle(type === 'tedarikci')}
        >
          🏢 Tedarikçiler
        </button>
      </div>

      <div style={{ background: colors.surface, borderRadius: radius.md, boxShadow: shadows.sm, padding: 16, marginBottom: 18 }}>
        <input
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder={`${type === 'musteri' ? 'Müşteri' : 'Tedarikçi'} ara (ünvan, kod, vergi no)...`}
          style={{ width: '100%', boxSizing: 'border-box', padding: '11px 14px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: 14 }}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: selected ? 'minmax(390px, 42%) 1fr' : '1fr', gap: 18, alignItems: 'start' }}>
        <div style={{ background: colors.surface, borderRadius: radius.md, boxShadow: shadows.sm, overflow: 'hidden' }}>
          <div style={{ padding: '14px 16px', fontWeight: 700, borderBottom: `1px solid ${colors.border}` }}>
            {type === 'musteri' ? 'Müşteriler' : 'Tedarikçiler'} <span style={{ color: colors.textSecondary, fontWeight: 400 }}>({filtered.length})</span>
          </div>
          <div style={{ maxHeight: 620, overflowY: 'auto' }}>
            {loading ? (
              <div style={{ padding: 30, textAlign: 'center', color: colors.textSecondary }}>Yükleniyor…</div>
            ) : filtered.length === 0 ? (
              <div style={{ padding: 30, textAlign: 'center', color: colors.textSecondary }}>Kayıt bulunamadı.</div>
            ) : filtered.map(item => {
              const active = selected?.id === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => selectItem(item)}
                  style={{
                    width: '100%', border: 0, borderBottom: `1px solid ${colors.border}`,
                    background: active ? '#fff3f3' : 'white', padding: '13px 16px', cursor: 'pointer',
                    textAlign: 'left', display: 'grid', gridTemplateColumns: '1fr auto', gap: 10,
                  }}
                >
                  <span>
                    <span style={{ display: 'block', fontWeight: 700, color: colors.textPrimary }}>{item.unvan || item.ad || '-'}</span>
                    <span style={{ display: 'block', marginTop: 4, color: colors.textSecondary, fontSize: 12 }}>
                      {[item.kod, item.vergi_no].filter(Boolean).join(' · ') || 'Detay için tıklayın'}
                    </span>
                  </span>
                  <span style={{ color: '#c62828', fontSize: 18, alignSelf: 'center' }}>›</span>
                </button>
              );
            })}
          </div>
        </div>

        {selected && (
          <div style={{ background: colors.surface, borderRadius: radius.md, boxShadow: shadows.sm, overflow: 'hidden' }}>
            <div style={{ padding: '16px 18px', borderBottom: `1px solid ${colors.border}` }}>
              <div style={{ fontSize: 12, color: colors.textSecondary, marginBottom: 4 }}>İşlem Listesi</div>
              <div style={{ fontWeight: 800, fontSize: 17 }}>{selected.unvan || selected.ad || '-'}</div>
            </div>

            {movementLoading ? (
              <div style={{ padding: 35, textAlign: 'center', color: colors.textSecondary }}>İşlemler yükleniyor…</div>
            ) : movementError ? (
              <div style={{ padding: 20, color: '#b42318' }}>{movementError}</div>
            ) : movements.length === 0 ? (
              <div style={{ padding: 35, textAlign: 'center', color: colors.textSecondary }}>Bu cari için işlem bulunamadı.</div>
            ) : (
              <div style={{ overflowX: 'auto', maxHeight: 620, overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                    <tr style={{ background: '#f3f5f8' }}>
                      <th style={th}>Tarih</th>
                      <th style={th}>İşlem</th>
                      <th style={th}>Belge</th>
                      <th style={th}>Açıklama</th>
                      <th style={{ ...th, textAlign: 'right' }}>Borç</th>
                      <th style={{ ...th, textAlign: 'right' }}>Alacak</th>
                      <th style={{ ...th, textAlign: 'right' }}>Bakiye</th>
                    </tr>
                  </thead>
                  <tbody>
                    {movements.map((m, idx) => (
                      <tr key={m.key || idx} style={{ borderBottom: `1px solid ${colors.border}` }}>
                        <td style={td}>{dateText(m.tarih)}</td>
                        <td style={td}><span style={{ fontWeight: 700 }}>{String(m.tip || '-').replaceAll('_', ' ')}</span></td>
                        <td style={td}>{m.belge || '-'}</td>
                        <td style={td}>{m.aciklama || '-'}</td>
                        <td style={{ ...td, textAlign: 'right', color: m.borc ? '#c62828' : colors.textSecondary }}>{m.borc ? money(m.borc) : '-'}</td>
                        <td style={{ ...td, textAlign: 'right', color: m.alacak ? '#16803a' : colors.textSecondary }}>{m.alacak ? money(m.alacak) : '-'}</td>
                        <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{m.bakiye == null ? '-' : money(m.bakiye)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const switchStyle = (active) => ({
  padding: '10px 16px', borderRadius: radius.sm, cursor: 'pointer', fontWeight: 700,
  border: active ? '1px solid #d32f2f' : `1px solid ${colors.border}`,
  background: active ? '#d32f2f' : 'white', color: active ? 'white' : colors.textPrimary,
});

const th = { padding: '10px 12px', textAlign: 'left', whiteSpace: 'nowrap', color: '#4b5563', fontSize: 12 };
const td = { padding: '11px 12px', verticalAlign: 'top', whiteSpace: 'nowrap' };

export default MusterilerTedarikcilerPage;
